import { editorFromSavedPieces, Piece, PieceEditorState } from '@/app/common/brief-pieces/piece-list';
import { SocialPlacementChannel } from '@/app/common/general-types/briefs.dto';

export type SmartBriefStep = 'source' | 'product' | 'pieces' | 'creators' | 'ready';

export type ComposerIntent = 'ignore' | 'need-more' | 'read-text' | 'revise';

/** What Send does for the current step. A short paste is not sent to the reader. */
export function composerIntent(step: SmartBriefStep, text: string, reading: boolean): ComposerIntent {
  const trimmed = text.trim();
  if (reading || trimmed.length === 0) {
    return 'ignore';
  }
  if (step === 'source') {
    return trimmed.length <= 12 ? 'need-more' : 'read-text';
  }
  if (step === 'ready') {
    return 'revise';
  }
  return 'ignore';
}

export function canCreateBrief(input: {
  step: SmartBriefStep;
  hasProduct: boolean;
  title: string;
  stepsHtml: string;
  doHtml: string;
  dontHtml: string;
  pieceCount: number;
}): boolean {
  return input.step === 'ready'
    && input.hasProduct
    && input.title.trim().length > 0
    && input.stepsHtml.trim().length > 0
    && input.doHtml.trim().length > 0
    && input.dontHtml.trim().length > 0
    && input.pieceCount > 0;
}

export type ExtractedBrief = {
  title: string;
  stepsHtml: string;
  doHtml: string;
  dontHtml: string;
  pieces: Piece[];
};

export type BriefDraftTarget = {
  basicInfoModel: { set: (value: { briefTitle: string }) => void };
  stepsHtml: { set: (value: string) => void };
  description: { set: (value: string) => void };
  doHtml: { set: (value: string) => void };
  dontHtml: { set: (value: string) => void };
  pieceEditor: { set: (value: PieceEditorState | null) => void };
  pieces: { set: (value: Piece[] | null) => void };
};

const CHANNEL_LABEL: Record<SocialPlacementChannel, string> = {
  tiktok_post: 'TikTok Post',
  instagram_reel: 'Instagram Reel',
  instagram_story: 'Instagram Story',
  youtube_shorts: 'YouTube Shorts',
  snapchat_spotlight: 'Snapchat Spotlight',
  snapchat_story: 'Snapchat Story',
};

/** A later read keeps a field the model left blank. */
export function mergeExtractedBrief(current: ExtractedBrief, next: ExtractedBrief): ExtractedBrief {
  return {
    title: next.title.trim() || current.title,
    stepsHtml: next.stepsHtml.trim() || current.stepsHtml,
    doHtml: next.doHtml.trim() || current.doHtml,
    dontHtml: next.dontHtml.trim() || current.dontHtml,
    pieces: next.pieces.length > 0 ? next.pieces : current.pieces,
  };
}

export function applyExtractedBrief(target: BriefDraftTarget, extracted: ExtractedBrief): void {
  target.basicInfoModel.set({ briefTitle: extracted.title });
  target.stepsHtml.set(extracted.stepsHtml);
  target.description.set(extracted.stepsHtml);
  target.doHtml.set(extracted.doHtml);
  target.dontHtml.set(extracted.dontHtml);
  target.pieceEditor.set(extracted.pieces.length > 0 ? editorFromSavedPieces(extracted.pieces) : null);
  target.pieces.set(extracted.pieces.length > 0 ? extracted.pieces : null);
}

export function pieceSummary(piece: Piece): string {
  const placements = piece.placements.map((placement) =>
    placement.kind === 'ugc' ? 'UGC' : CHANNEL_LABEL[placement.channel],
  );
  return `${piece.label}: ${placements.join(' + ')}`;
}

export function currentExtractedBrief(source: {
  title: string;
  stepsHtml: string;
  doHtml: string;
  dontHtml: string;
  pieces: Piece[] | null;
}): ExtractedBrief {
  return {
    title: source.title,
    stepsHtml: source.stepsHtml,
    doHtml: source.doHtml,
    dontHtml: source.dontHtml,
    pieces: source.pieces ?? [],
  };
}
